import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ArrowLeft, Plus, Trash2, Save, Send, FileText, User, Calendar,
  Search, Building2, UserCircle, X, Check, CreditCard, Banknote, MapPin, Settings, Edit2,
  Coins, Gift, AlertTriangle, Shield, Pen
} from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Catégories de produits pour la fidélité
const PRODUCT_CATEGORIES = [
  { value: 'reparation', label: 'Réparation', color: 'bg-blue-100 text-blue-700' },
  { value: 'accessoire', label: 'Accessoire', color: 'bg-green-100 text-green-700' },
  { value: 'occasion', label: 'Téléphone occasion', color: 'bg-orange-100 text-orange-700' },
  { value: 'neuf', label: 'Téléphone neuf', color: 'bg-purple-100 text-purple-700' },
  { value: 'dematerialise', label: 'Dématérialisé (exclu)', color: 'bg-gray-100 text-gray-500' },
  { value: 'autre', label: 'Autre', color: 'bg-slate-100 text-slate-700' }
];

export default function InvoiceWarrantyFormPage({ user }) {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const canvasRef = useRef(null);
  const searchRef = useRef(null);
  const addressRef = useRef(null);

  // États principaux
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [clients, setClients] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [priceMode, setPriceMode] = useState('ht');

  // Client search
  const [clientSearch, setClientSearch] = useState('');
  const [showClientResults, setShowClientResults] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // Fidélité
  const [clientPoints, setClientPoints] = useState(null);
  const [loyaltySettings, setLoyaltySettings] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('reparation');
  const [usePoints, setUsePoints] = useState(false);
  const [pointsToUse, setPointsToUse] = useState(0);
  const [loyaltyDiscount, setLoyaltyDiscount] = useState(0);
  const [discountOptions, setDiscountOptions] = useState([]);
  const [showPointsOnInvoice, setShowPointsOnInvoice] = useState(false);

  // Address autocomplete
  const [addressSearch, setAddressSearch] = useState('');
  const [addressResults, setAddressResults] = useState([]);
  const [showAddressResults, setShowAddressResults] = useState(false);

  // Signature
  const [showSignatureSection, setShowSignatureSection] = useState(false);
  const [signatureMethod, setSignatureMethod] = useState('canvas');
  const [otpMethods, setOtpMethods] = useState({ sms: true, email: true });
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signatureData, setSignatureData] = useState(null);

  const [formData, setFormData] = useState({
    client_id: '',
    issue_date: format(new Date(), 'yyyy-MM-dd'),
    due_date: '',
    payment_terms: 'a_reception',
    payment_method: '',
    notes: '',
    terms: 'Paiement à réception de facture',
    title: '',
    is_paid: false,
    items: [
      { description: '', quantity: 1, unit_price: 0, tva_rate: 20, discount: 0 }
    ]
  });

  const [newClient, setNewClient] = useState({
    client_type: 'particulier',
    name: '',
    email: '',
    phone: '',
    address: '',
    postal_code: '',
    city: '',
    siret: '',
    tva_number: ''
  });

  // Chargement initial
  useEffect(() => {
    fetchClients();
    fetchCompanyInfo();
    fetchCompanySettings();
    fetchLoyaltySettings();
  }, []);

  // Close dropdowns
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowClientResults(false);
      }
      if (addressRef.current && !addressRef.current.contains(e.target)) {
        setShowAddressResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Canvas init
  useEffect(() => {
    if (showSignatureSection && signatureMethod === 'canvas' && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      ctx.strokeStyle = '#1e3a5f';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  }, [showSignatureSection, signatureMethod]);

  // Fetch functions
  const fetchClients = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/company-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanyInfo(response.data);
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/company-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanySettings(response.data);
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  };

  const fetchLoyaltySettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/loyalty/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoyaltySettings(response.data);
    } catch (error) {
      console.error('Error fetching loyalty settings:', error);
    }
  };

  const fetchClientPoints = async (clientId) => {
    if (!clientId) {
      setClientPoints(null);
      setDiscountOptions([]);
      return;
    }
    try {
      const response = await axios.get(`${BACKEND_URL}/api/loyalty/points/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientPoints(response.data);
      setUsePoints(false);
      setPointsToUse(0);
      setLoyaltyDiscount(0);
    } catch (error) {
      console.error('Error fetching client points:', error);
      setClientPoints(null);
    }
  };

  const calculateDiscountOptions = async (totalTTC, category) => {
    if (!selectedClient?.id || !totalTTC || totalTTC <= 0) {
      setDiscountOptions([]);
      return;
    }
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/loyalty/clients/${selectedClient.id}/calculate-discount?sale_amount=${totalTTC}&category=${category}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDiscountOptions(response.data.options || []);
    } catch (error) {
      console.error('Error calculating discount:', error);
      setDiscountOptions([]);
    }
  };

  const applyDiscountOption = (option) => {
    if (option) {
      setUsePoints(true);
      setPointsToUse(option.points_needed);
      setLoyaltyDiscount(option.discount_value);
    } else {
      setUsePoints(false);
      setPointsToUse(0);
      setLoyaltyDiscount(0);
    }
  };

  useEffect(() => {
    if (selectedClient?.id) {
      fetchClientPoints(selectedClient.id);
    } else {
      setClientPoints(null);
    }
  }, [selectedClient?.id]);

  useEffect(() => {
    const totals = calculateTotals();
    if (selectedClient?.id && totals.total > 0 && selectedCategory !== 'dematerialise') {
      calculateDiscountOptions(totals.total, selectedCategory);
    } else {
      setDiscountOptions([]);
    }
  }, [formData.items, selectedCategory, selectedClient?.id]);

  // Address autocomplete
  const searchAddress = async (query) => {
    if (query.length < 3) {
      setAddressResults([]);
      return;
    }
    try {
      const response = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=5`
      );
      const data = await response.json();
      setAddressResults(data.features || []);
      setShowAddressResults(true);
    } catch (error) {
      console.error('Address search error:', error);
    }
  };

  const handleSelectAddress = (feature) => {
    const props = feature.properties;
    setNewClient({
      ...newClient,
      address: props.name || '',
      postal_code: props.postcode || '',
      city: props.city || ''
    });
    setAddressSearch(props.label || '');
    setShowAddressResults(false);
  };

  // Client handlers
  const filteredClients = clients.filter(client => {
    const search = clientSearch.toLowerCase();
    return (
      client.name?.toLowerCase().includes(search) ||
      client.siret?.toLowerCase().includes(search) ||
      client.email?.toLowerCase().includes(search) ||
      client.phone?.includes(search)
    );
  }).slice(0, 10);

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setFormData({ ...formData, client_id: client.id });
    setClientSearch(client.name);
    setShowClientResults(false);
  };

  const handleClearClient = () => {
    setSelectedClient(null);
    setFormData({ ...formData, client_id: '' });
    setClientSearch('');
  };

  // Item handlers
  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0, tva_rate: 20, discount: 0 }]
    });
  };

  const handleRemoveItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData({ ...formData, items: newItems });
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    if (field === 'description') {
      newItems[index][field] = value;
    } else if (field === 'unit_price' && priceMode === 'ttc') {
      const ttcValue = parseFloat(value) || 0;
      const tvaRate = newItems[index].tva_rate || 20;
      newItems[index][field] = ttcValue / (1 + tvaRate / 100);
    } else {
      newItems[index][field] = parseFloat(value) || 0;
    }
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotals = () => {
    let totalHT = 0;
    let totalTVA = 0;
    formData.items.forEach(item => {
      const itemHT = item.quantity * item.unit_price * (1 - item.discount / 100);
      const itemTVA = itemHT * item.tva_rate / 100;
      totalHT += itemHT;
      totalTVA += itemTVA;
    });
    return {
      totalHT: totalHT.toFixed(2),
      totalTVA: totalTVA.toFixed(2),
      totalTTC: (totalHT + totalTVA).toFixed(2),
      total: totalHT + totalTVA
    };
  };

  // Create client
  const handleCreateClient = async () => {
    if (!newClient.name) {
      toast.error('Le nom du client est obligatoire');
      return;
    }
    try {
      const response = await axios.post(`${BACKEND_URL}/api/clients`, newClient, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Client créé avec succès');
      const clientId = response.data.id;
      const createdClient = { id: clientId, ...newClient };
      handleSelectClient(createdClient);
      fetchClients();
      setShowNewClientModal(false);
      setNewClient({
        client_type: 'particulier',
        name: '', email: '', phone: '', address: '',
        postal_code: '', city: '', siret: '', tva_number: ''
      });
      setAddressSearch('');
    } catch (error) {
      toast.error('Erreur lors de la création du client');
    }
  };

  // Signature handlers
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const coords = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const coords = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      setHasSignature(true);
      setSignatureData(canvasRef.current.toDataURL('image/png'));
    }
  };

  const clearSignature = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasSignature(false);
    setSignatureData(null);
  };

  // Validation
  const validateForm = () => {
    if (!selectedClient) {
      toast.error('Veuillez sélectionner un client');
      return false;
    }
    if (formData.items.some(item => !item.description || item.unit_price <= 0)) {
      toast.error('Veuillez remplir correctement toutes les lignes');
      return false;
    }
    if (!formData.payment_method) {
      toast.error('Veuillez sélectionner un mode de paiement');
      return false;
    }
    return true;
  };

  const handleProceedToSignature = () => {
    if (!validateForm()) return;
    setShowSignatureSection(true);
  };

  // Submit avec signature canvas (sur place)
  const handleSubmit = async () => {
    if (!hasSignature || !signatureData) {
      toast.error('La signature du client est obligatoire');
      return;
    }

    setLoading(true);
    try {
      const totals = calculateTotals();
      const payload = {
        invoice_type: 'invoice',
        status: formData.is_paid ? 'paid' : 'sent',
        client_id: selectedClient.id,
        client_name: selectedClient.name,
        client_email: selectedClient.email || '',
        client_phone: selectedClient.phone || '',
        client_address: selectedClient.address ? 
          `${selectedClient.address}${selectedClient.postal_code ? ', ' + selectedClient.postal_code : ''}${selectedClient.city ? ' ' + selectedClient.city : ''}` : '',
        items: formData.items.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price),
          tva_rate: parseFloat(item.tva_rate),
          discount: parseFloat(item.discount) || 0
        })),
        issue_date: formData.issue_date,
        payment_method: formData.payment_method,
        notes: formData.notes,
        // Fidélité
        category: selectedCategory,
        loyalty_points_used: usePoints ? pointsToUse : null,
        loyalty_discount: usePoints ? loyaltyDiscount : null,
        show_points_on_invoice: showPointsOnInvoice,
        client_points_balance: showPointsOnInvoice ? (clientPoints?.points_balance || 0) : null,
        // Garantie
        is_warranty_invoice: true,
        warranty_signature: signatureData,
        warranty_signature_date: new Date().toISOString(),
        warranty_conditions_accepted: true
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/invoices/warranty/create`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      let successMsg = 'Facture avec garantie créée avec succès';
      if (response.data.points_earned && response.data.points_earned > 0) {
        successMsg += ` (+${response.data.points_earned} points fidélité)`;
      }
      toast.success(successMsg);
      navigate(`/invoices/${response.data.id}?download=true`);
    } catch (error) {
      console.error('Error creating warranty invoice:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  // Submit avec OTP (à distance)
  const handleSubmitWithOTP = async () => {
    if (!otpMethods.sms && !otpMethods.email) {
      toast.error('Veuillez sélectionner au moins une méthode d\'envoi');
      return;
    }

    if (!selectedClient?.phone && !selectedClient?.email) {
      toast.error('Le client n\'a pas de contact (téléphone ou email)');
      return;
    }

    setLoading(true);
    try {
      const totals = calculateTotals();
      const payload = {
        invoice_type: 'invoice',
        status: 'pending_signature',
        client_id: selectedClient.id,
        client_name: selectedClient.name,
        client_email: selectedClient.email || '',
        client_phone: selectedClient.phone || '',
        client_address: selectedClient.address ? 
          `${selectedClient.address}${selectedClient.postal_code ? ', ' + selectedClient.postal_code : ''}${selectedClient.city ? ' ' + selectedClient.city : ''}` : '',
        items: formData.items.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price),
          tva_rate: parseFloat(item.tva_rate),
          discount: parseFloat(item.discount) || 0
        })),
        issue_date: formData.issue_date,
        payment_method: formData.payment_method,
        notes: formData.notes,
        category: selectedCategory,
        is_warranty_invoice: true,
        warranty_signature: '',
        warranty_signature_date: '',
        warranty_conditions_accepted: false
      };

      // 1. Créer la facture
      const createResponse = await axios.post(
        `${BACKEND_URL}/api/invoices/warranty/create`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const invoiceId = createResponse.data.id;

      // 2. Envoyer la demande de signature OTP
      const method = (otpMethods.sms && otpMethods.email) ? 'both' : (otpMethods.sms ? 'sms' : 'email');
      await axios.post(
        `${BACKEND_URL}/api/invoices/${invoiceId}/warranty/request-signature`,
        { method },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Facture créée ! Lien de signature envoyé au client.');
      navigate('/invoices');
    } catch (error) {
      console.error('Error creating warranty invoice with OTP:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();
  const paymentMethods = ['Espèces', 'Carte bancaire', 'Mixte', 'Virement'];

  // Section signature
  if (showSignatureSection) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 md:p-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => setShowSignatureSection(false)}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour au formulaire
            </Button>
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-emerald-400" />
              <div>
                <h1 className="text-2xl font-bold text-white">Signature & Garantie</h1>
                <p className="text-white/60">Le client doit signer pour valider les conditions</p>
              </div>
            </div>
          </div>

          {/* Résumé */}
          <div className="bg-white rounded-xl p-6">
            <h2 className="text-lg font-bold text-slate-800 mb-4">Résumé de la facture</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Client</span>
                <p className="font-medium">{selectedClient?.name}</p>
              </div>
              <div>
                <span className="text-slate-500">Total HT</span>
                <p className="font-medium">{totals.totalHT} €</p>
              </div>
              <div>
                <span className="text-slate-500">TVA</span>
                <p className="font-medium">{totals.totalTVA} €</p>
              </div>
              <div>
                <span className="text-slate-500">Total TTC</span>
                <p className="font-bold text-xl text-green-600">{totals.totalTTC} €</p>
              </div>
            </div>
          </div>

          {/* Conditions de garantie */}
          <div className="bg-white rounded-xl p-6">
            <h2 className="text-lg font-bold text-blue-900 mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600" />
              CONDITIONS DE GARANTIE
            </h2>
            
            <div className="space-y-3 max-h-72 overflow-y-auto pr-2">
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <h4 className="font-bold text-green-800 text-sm">1. RÉFÉRENTIEL DES GARANTIES</h4>
                <ul className="text-xs text-green-700 mt-1 space-y-0.5">
                  <li>• Appareils Neufs / Reconditionnés : <strong>24 mois</strong></li>
                  <li>• Pièces d'Origine (Service Pack) : <strong>12 mois</strong></li>
                  <li>• Compatibles Premium : <strong>6 mois</strong></li>
                  <li>• Entrée de Gamme : <strong>3 mois minimum</strong></li>
                </ul>
                <p className="text-xs text-green-600 mt-1">⚖️ Art. L217-11 : Immobilisation &gt; 7 jours = Extension automatique</p>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                <h4 className="font-bold text-emerald-800 text-sm">2. CE QUI EST PRIS EN CHARGE</h4>
                <p className="text-xs text-emerald-700">Vices de fabrication et défauts internes : Affichage, Tactile, Batterie, Composants</p>
              </div>

              <div className="bg-red-50 border-2 border-red-300 rounded p-3">
                <h4 className="font-bold text-red-800 text-sm">3. CE QUI EST EXCLU (REJET)</h4>
                <ul className="text-xs text-red-600 space-y-0.5">
                  <li><strong>A. Écran :</strong> Lignes colorées, taches noires, fissure</li>
                  <li><strong>B. Physique :</strong> Vitre brisée, châssis tordu, coins enfoncés</li>
                  <li><strong>C. Intervention Tierce :</strong> Vis marquées, sticker rompu, pièce démontée</li>
                  <li><strong>D. Oxydation :</strong> Pastilles rouges, corrosion, pins tordus</li>
                </ul>
              </div>

              <div className="bg-slate-100 border border-slate-300 rounded p-3">
                <h4 className="font-bold text-slate-800 text-sm">4. CLAUSES JURIDIQUES</h4>
                <ul className="text-xs text-slate-700 space-y-0.5">
                  <li>📜 Exclusivité: Pièce DOIT rester montée</li>
                  <li>📜 Données (Art. 1231-1): Client responsable de la sauvegarde</li>
                  <li>📜 Rétention (Art. 2286): Appareil conservé jusqu'au paiement</li>
                </ul>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded p-3">
                <h4 className="font-bold text-purple-800 text-sm">5. LOGIQUE SYSTÈME</h4>
                <ul className="text-xs text-purple-700 space-y-0.5">
                  <li>📸 3 photos obligatoires (Face, Dos, Profil)</li>
                  <li>✍️ Signature électronique obligatoire</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Zone de signature */}
          <div className="bg-white rounded-xl p-6">
            <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-4">
              <Pen className="w-5 h-5 text-blue-600" />
              Méthode de signature
            </h3>

            {/* Choix de la méthode */}
            <div className="flex gap-3 mb-6">
              <button
                onClick={() => setSignatureMethod('canvas')}
                className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                  signatureMethod === 'canvas' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <Pen className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                <p className="font-medium text-slate-800">Signature sur place</p>
                <p className="text-xs text-slate-500">Le client signe directement sur l'écran</p>
              </button>
              <button
                onClick={() => setSignatureMethod('otp')}
                className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                  signatureMethod === 'otp' 
                    ? 'border-purple-500 bg-purple-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <Send className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                <p className="font-medium text-slate-800">Signature à distance</p>
                <p className="text-xs text-slate-500">Envoi d'un lien OTP par SMS/Email</p>
              </button>
            </div>

            {/* Signature Canvas */}
            {signatureMethod === 'canvas' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Dessinez la signature ci-dessous</span>
                  <button
                    onClick={clearSignature}
                    className="text-sm text-slate-600 hover:text-slate-800 flex items-center gap-1"
                  >
                    <X className="w-4 h-4" /> Effacer
                  </button>
                </div>

                <canvas
                  ref={canvasRef}
                  width={600}
                  height={180}
                  className="w-full border-2 border-slate-300 rounded-lg cursor-crosshair bg-white touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />

                <p className="text-xs text-slate-500 mt-2 text-center">
                  En signant, le client confirme avoir lu et accepté les conditions de garantie ci-dessus.
                </p>

                {hasSignature && (
                  <div className="mt-3 flex items-center gap-2 text-green-600">
                    <Check className="w-5 h-5" />
                    <span className="font-medium">Signature enregistrée</span>
                  </div>
                )}
              </div>
            )}

            {/* Signature OTP */}
            {signatureMethod === 'otp' && (
              <div className="space-y-4">
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-sm text-purple-800 mb-2">
                    <strong>Mode signature à distance :</strong>
                  </p>
                  <p className="text-xs text-purple-700">
                    La facture sera créée en attente de signature. Un lien avec code OTP sera envoyé au client.
                  </p>
                </div>

                {!selectedClient?.phone && !selectedClient?.email && (
                  <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <span className="text-sm text-amber-800">Le client n'a pas de téléphone ni d'email.</span>
                  </div>
                )}

                {(selectedClient?.phone || selectedClient?.email) && (
                  <div className="space-y-2">
                    <p className="text-sm text-slate-600">Envoyer le lien de signature via :</p>
                    <div className="flex gap-4">
                      {selectedClient?.phone && (
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={otpMethods.sms}
                            onChange={(e) => setOtpMethods({ ...otpMethods, sms: e.target.checked })}
                            className="rounded border-slate-300"
                          />
                          <span className="text-sm">SMS ({selectedClient.phone})</span>
                        </label>
                      )}
                      {selectedClient?.email && (
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={otpMethods.email}
                            onChange={(e) => setOtpMethods({ ...otpMethods, email: e.target.checked })}
                            className="rounded border-slate-300"
                          />
                          <span className="text-sm">Email ({selectedClient.email})</span>
                        </label>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Bouton créer */}
          <Button
            onClick={signatureMethod === 'canvas' ? handleSubmit : handleSubmitWithOTP}
            disabled={loading || (signatureMethod === 'canvas' && !hasSignature) || (signatureMethod === 'otp' && !otpMethods.sms && !otpMethods.email)}
            className={`w-full h-14 text-lg ${
              (signatureMethod === 'canvas' && hasSignature) || (signatureMethod === 'otp' && (otpMethods.sms || otpMethods.email))
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-slate-400 cursor-not-allowed'
            }`}
          >
            {loading ? (
              'Création en cours...'
            ) : signatureMethod === 'canvas' ? (
              <>
                <Check className="w-6 h-6 mr-2" />
                Créer la facture avec garantie
              </>
            ) : (
              <>
                <Send className="w-6 h-6 mr-2" />
                Créer et envoyer lien de signature
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Formulaire principal
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/invoices')} variant="ghost" size="sm" className="text-white hover:bg-white/20">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6" />
              <h1 className="font-heading text-xl font-bold">Nouvelle Facture avec Garantie</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => navigate('/settings')}
              variant="ghost"
              className="text-white hover:bg-white/20"
            >
              <Settings className="w-4 h-4 mr-2" />
              Paramètres
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 md:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Form */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Company Info Header */}
            {companyInfo && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-start gap-4">
                  {companySettings?.logo_url ? (
                    <img 
                      src={`${BACKEND_URL}${companySettings.logo_url}`} 
                      alt="Logo" 
                      className="w-20 h-20 object-contain"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center">
                      <Building2 className="w-10 h-10 text-emerald-600" />
                    </div>
                  )}
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">{companyInfo.legal_name || companyInfo.name}</h2>
                    <p className="text-sm text-slate-500">{companyInfo.address}</p>
                    <p className="text-sm text-slate-500">{companyInfo.postal_code} {companyInfo.city}</p>
                    <p className="text-sm text-slate-500">{companyInfo.phone}</p>
                  </div>
                  <Badge className="ml-auto bg-emerald-100 text-emerald-700 border-emerald-200">
                    <Shield className="w-3 h-3 mr-1" />
                    GARANTIE
                  </Badge>
                </div>
              </div>
            )}

            {/* Client Selection */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5" />
                Client
              </h2>

              <div className="space-y-4">
                <div className="relative" ref={searchRef}>
                  <Label>Rechercher un client par nom, SIREN ou SIRET</Label>
                  <div className="relative mt-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={clientSearch}
                      onChange={(e) => {
                        setClientSearch(e.target.value);
                        setShowClientResults(true);
                        if (!e.target.value) handleClearClient();
                      }}
                      onFocus={() => setShowClientResults(true)}
                      placeholder="Rechercher votre client..."
                      className="pl-10 pr-10"
                    />
                    {selectedClient && (
                      <button onClick={handleClearClient} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {showClientResults && clientSearch && !selectedClient && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-auto">
                      {filteredClients.length > 0 ? (
                        filteredClients.map(client => (
                          <button
                            key={client.id}
                            onClick={() => handleSelectClient(client)}
                            className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                client.client_type === 'professionnel' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                              }`}>
                                {client.client_type === 'professionnel' ? <Building2 className="w-4 h-4" /> : <UserCircle className="w-4 h-4" />}
                              </div>
                              <div>
                                <div className="font-medium text-slate-900">{client.name}</div>
                                <div className="text-xs text-slate-500">
                                  {client.siret && `SIRET: ${client.siret} • `}
                                  {client.email || client.phone || 'Pas de contact'}
                                </div>
                              </div>
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-sm text-slate-500">Aucun client trouvé</div>
                      )}
                      <button
                        onClick={() => { setShowClientResults(false); setShowNewClientModal(true); }}
                        className="w-full px-4 py-3 text-left hover:bg-green-50 text-green-600 font-medium border-t border-slate-200 flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Créer un nouveau client
                      </button>
                    </div>
                  )}
                </div>

                {selectedClient && (
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          selectedClient.client_type === 'professionnel' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                        }`}>
                          {selectedClient.client_type === 'professionnel' ? <Building2 className="w-5 h-5" /> : <UserCircle className="w-5 h-5" />}
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">{selectedClient.name}</div>
                          <div className="text-sm text-slate-500">
                            {selectedClient.phone && <span className="mr-2">{selectedClient.phone}</span>}
                            {selectedClient.email && <span>{selectedClient.email}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/clients/${selectedClient.id}`)} className="text-blue-600">
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={handleClearClient}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {!selectedClient && (
                  <Button onClick={() => setShowNewClientModal(true)} variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Nouveau client
                  </Button>
                )}
              </div>
            </div>

            {/* Title */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <Label>Intitulé de la facture</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Intitulé de la facture"
                className="mt-1"
              />
            </div>

            {/* Items */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Prestations / Produits
                </h2>
                
                <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
                  <button
                    onClick={() => setPriceMode('ht')}
                    className={`px-3 py-1 rounded text-sm font-medium transition ${
                      priceMode === 'ht' ? 'bg-white shadow text-slate-900' : 'text-slate-500'
                    }`}
                  >
                    Prix HT
                  </button>
                  <button
                    onClick={() => setPriceMode('ttc')}
                    className={`px-3 py-1 rounded text-sm font-medium transition ${
                      priceMode === 'ttc' ? 'bg-white shadow text-slate-900' : 'text-slate-500'
                    }`}
                  >
                    Prix TTC
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                {formData.items.map((item, index) => (
                  <div key={index} className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                    <div className="mb-3">
                      <Label className="text-xs text-slate-500 mb-1 block">Désignation</Label>
                      <textarea
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        placeholder="Description détaillée du produit ou service..."
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-y min-h-[60px] focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        rows={2}
                      />
                    </div>
                    
                    <div className="grid grid-cols-12 gap-3 items-center">
                      <div className="col-span-4 md:col-span-3">
                        <Label className="text-xs text-slate-500">Quantité</Label>
                        <Input
                          type="number"
                          min="1"
                          step="0.01"
                          value={item.quantity || ''}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          className="text-center"
                        />
                      </div>
                      <div className="col-span-4 md:col-span-3">
                        <Label className="text-xs text-slate-500">Prix {priceMode.toUpperCase()} (€)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={priceMode === 'ht' 
                            ? (item.unit_price || '') 
                            : (item.unit_price ? Number((item.unit_price * (1 + item.tva_rate / 100)).toFixed(2)) : '')
                          }
                          onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                          className="text-center"
                        />
                      </div>
                      <div className="col-span-3 md:col-span-3">
                        <Label className="text-xs text-slate-500">TVA (%)</Label>
                        <select
                          value={item.tva_rate}
                          onChange={(e) => handleItemChange(index, 'tva_rate', e.target.value)}
                          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-center"
                        >
                          <option value="0">0%</option>
                          <option value="5.5">5.5%</option>
                          <option value="10">10%</option>
                          <option value="20">20%</option>
                        </select>
                      </div>
                      <div className="col-span-1 md:col-span-3 flex justify-end items-end">
                        <Button
                          onClick={() => handleRemoveItem(index)}
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          disabled={formData.items.length === 1}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-2 text-right text-sm text-slate-500">
                      Montant HT: <span className="font-medium text-slate-700">
                        {(item.quantity * item.unit_price * (1 - item.discount / 100)).toFixed(2)} €
                      </span>
                      {' '}| TTC: <span className="font-medium text-slate-700">
                        {(item.quantity * item.unit_price * (1 - item.discount / 100) * (1 + item.tva_rate / 100)).toFixed(2)} €
                      </span>
                    </div>
                  </div>
                ))}

                <Button onClick={handleAddItem} variant="outline" size="sm" className="mt-2">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter une ligne
                </Button>
              </div>
            </div>

            {/* Payment Method & Dates */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Paiement & Conditions
              </h2>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Mode de paiement</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {paymentMethods.map((method) => (
                      <button
                        key={method}
                        onClick={() => setFormData({ ...formData, payment_method: method })}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition ${
                          formData.payment_method === method
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        {method === 'Carte bancaire' && <CreditCard className="w-4 h-4" />}
                        {method === 'Espèces' && <Banknote className="w-4 h-4" />}
                        {method === 'Mixte' && <><CreditCard className="w-3 h-3" /><Banknote className="w-3 h-3" /></>}
                        {method === 'Virement' && <Building2 className="w-4 h-4" />}
                        {method}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="col-span-2">
                  <label className="flex items-center gap-3 cursor-pointer p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition">
                    <input
                      type="checkbox"
                      checked={formData.is_paid}
                      onChange={(e) => setFormData({ ...formData, is_paid: e.target.checked })}
                      className="w-5 h-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <div>
                      <span className="font-medium text-slate-800">Marquer comme PAYÉ</span>
                      <p className="text-sm text-slate-500">La facture sera enregistrée avec le statut "Payé"</p>
                    </div>
                    {formData.is_paid && (
                      <span className="ml-auto px-3 py-1 bg-emerald-500 text-white text-sm font-bold rounded-full">
                        ✓ PAYÉ
                      </span>
                    )}
                  </label>
                </div>
                
                <div>
                  <Label>Date d'émission</Label>
                  <Input
                    type="date"
                    value={formData.issue_date}
                    onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Règlement</Label>
                  <select
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-4 py-2"
                  >
                    <option value="a_reception">À réception</option>
                    <option value="15_jours">15 jours</option>
                    <option value="30_jours">30 jours</option>
                    <option value="45_jours">45 jours fin de mois</option>
                    <option value="60_jours">60 jours</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <Label>Notes</Label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Notes additionnelles..."
                    className="w-full border border-slate-200 rounded-lg px-4 py-2 min-h-[80px]"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-slate-200 p-6 sticky top-24">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Récapitulatif</h2>

              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Total HT</span>
                  <span className="font-medium">{totals.totalHT} €</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">TVA</span>
                  <span className="font-medium">{totals.totalTVA} €</span>
                </div>
                
                {usePoints && loyaltyDiscount > 0 && (
                  <div className="flex justify-between text-sm text-amber-600">
                    <span className="flex items-center gap-1">
                      <Gift className="w-3 h-3" />
                      Remise fidélité (-{pointsToUse} pts)
                    </span>
                    <span className="font-medium">-{loyaltyDiscount.toFixed(2)} €</span>
                  </div>
                )}
                
                <div className="flex justify-between text-xl font-bold pt-3 border-t border-slate-200">
                  <span>Total TTC</span>
                  <span className="text-emerald-600">
                    {(parseFloat(totals.totalTTC) - (usePoints ? loyaltyDiscount : 0)).toFixed(2)} €
                  </span>
                </div>
                
                {selectedCategory !== 'dematerialise' && parseFloat(totals.totalTTC) > 0 && (
                  <div className="text-sm text-emerald-600 flex items-center gap-1">
                    <Coins className="w-4 h-4" />
                    +{Math.floor(parseFloat(totals.totalTTC) - (usePoints ? loyaltyDiscount : 0))} points fidélité
                  </div>
                )}
              </div>

              {/* Points du client */}
              {selectedClient && clientPoints && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-amber-800 flex items-center gap-1">
                      <Coins className="w-4 h-4" />
                      Solde fidélité
                    </span>
                    <Badge className="bg-amber-100 text-amber-700 text-lg font-bold">
                      {clientPoints.points_balance || 0} pts
                    </Badge>
                  </div>
                  
                  <div className="mb-3">
                    <Label className="text-xs text-amber-700">Catégorie</Label>
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger className="mt-1 h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRODUCT_CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            <span className={`px-2 py-0.5 rounded ${cat.color}`}>{cat.label}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {discountOptions.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-xs text-amber-700">Utiliser les points</Label>
                      {discountOptions.map((option, idx) => (
                        <button
                          key={idx}
                          onClick={() => applyDiscountOption(usePoints && pointsToUse === option.points_needed ? null : option)}
                          className={`w-full p-2 rounded border text-left text-sm transition ${
                            usePoints && pointsToUse === option.points_needed
                              ? 'border-amber-500 bg-amber-100'
                              : 'border-amber-200 hover:bg-amber-50'
                          }`}
                        >
                          <div className="flex justify-between">
                            <span>{option.points_needed} pts</span>
                            <span className="font-bold text-amber-700">-{option.discount_value.toFixed(2)} €</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Indicateur Garantie */}
              <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-center gap-2 text-emerald-700">
                  <Shield className="w-5 h-5" />
                  <span className="font-medium">Facture avec Garantie</span>
                </div>
                <p className="text-xs text-emerald-600 mt-1">
                  La signature du client sera requise avant la création.
                </p>
              </div>

              {/* Bouton Continuer vers signature */}
              <Button
                onClick={handleProceedToSignature}
                className="w-full mt-6 bg-emerald-600 hover:bg-emerald-700"
                disabled={!selectedClient || formData.items.some(i => !i.description || i.unit_price <= 0)}
              >
                <Shield className="w-4 h-4 mr-2" />
                Continuer vers la signature
              </Button>
            </div>
          </div>
        </div>
      </main>

      {/* New Client Modal */}
      {showNewClientModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900">Nouveau client</h2>
                <button onClick={() => setShowNewClientModal(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex gap-2">
                <button
                  onClick={() => setNewClient({ ...newClient, client_type: 'particulier' })}
                  className={`flex-1 py-2 rounded-lg border ${
                    newClient.client_type === 'particulier' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200'
                  }`}
                >
                  Particulier
                </button>
                <button
                  onClick={() => setNewClient({ ...newClient, client_type: 'professionnel' })}
                  className={`flex-1 py-2 rounded-lg border ${
                    newClient.client_type === 'professionnel' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200'
                  }`}
                >
                  Professionnel
                </button>
              </div>

              <div>
                <Label>Nom *</Label>
                <Input
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  placeholder="Nom du client"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newClient.email}
                    onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                    placeholder="email@exemple.com"
                  />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input
                    value={newClient.phone}
                    onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                    placeholder="06 12 34 56 78"
                  />
                </div>
              </div>

              <div className="relative" ref={addressRef}>
                <Label>Adresse</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={addressSearch}
                    onChange={(e) => {
                      setAddressSearch(e.target.value);
                      searchAddress(e.target.value);
                    }}
                    placeholder="Rechercher une adresse..."
                    className="pl-10"
                  />
                </div>
                {showAddressResults && addressResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                    {addressResults.map((result, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSelectAddress(result)}
                        className="w-full px-4 py-2 text-left hover:bg-slate-50 text-sm"
                      >
                        {result.properties.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {newClient.client_type === 'professionnel' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>SIRET</Label>
                    <Input
                      value={newClient.siret}
                      onChange={(e) => setNewClient({ ...newClient, siret: e.target.value })}
                      placeholder="123 456 789 00012"
                    />
                  </div>
                  <div>
                    <Label>N° TVA</Label>
                    <Input
                      value={newClient.tva_number}
                      onChange={(e) => setNewClient({ ...newClient, tva_number: e.target.value })}
                      placeholder="FR12345678901"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-200 flex gap-3">
              <Button variant="outline" onClick={() => setShowNewClientModal(false)} className="flex-1">
                Annuler
              </Button>
              <Button onClick={handleCreateClient} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                <Check className="w-4 h-4 mr-2" />
                Créer le client
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
